import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getPayoff } from '../lib/payoff';
import { LEGAL_IDENTITY } from '../config/legalIdentity';
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';

const Footer = () => {
  const { t, language } = useLanguage();
  const payoff = getPayoff(language);

  return (
    <footer className="bg-gray-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Brand payoff — featured at top of footer */}
        <div className="text-center mb-10 pb-8 border-b border-gray-800" data-testid="footer-payoff">
          <p className="text-xl md:text-2xl lg:text-3xl tracking-tight italic text-[#C9A84C] leading-snug max-w-4xl mx-auto" style={{fontFamily: "'Cormorant Garamond', serif"}}>
            {payoff.short}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-1">
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_return-dialog/artifacts/cartnx7j_7924d478-99b6-4d36-83f9-c10117d2477f.jpg"
                alt="Italian Roots Discovery"
                className="h-14 w-14 object-contain"
              />
              <div>
                <span className="font-bold text-white" style={{fontFamily: "'Cormorant Garamond', serif"}}>ITALIAN ROOTS</span>
                <span className="block text-xs text-blue-400 font-medium">DISCOVERY</span>
              </div>
            </div>
            <p className="text-gray-400 text-sm max-w-xs mb-4">
              {t.footer.description}
            </p>
            <p className="text-gray-500 text-xs">
              Laura Stella — Founder | Writer & Genealogical Research Specialist
            </p>
            <p className="text-gray-500 text-xs">
              Giosia Caliò — Co-Founder | Genealogical Research Specialist | Translator
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4">{t.footer.quickLinks}</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm">{t.nav.home}</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm">{t.nav.services}</Link></li>
              <li><Link to="/free-evaluation" className="text-green-400 hover:text-green-300 transition-colors font-medium text-sm">🎁 {language === 'it' ? 'Valutazione Gratuita' : language === 'es' ? 'Evaluación Gratuita' : language === 'pt' ? 'Avaliação Gratuita' : 'Free Evaluation'}</Link></li>
              <li><Link to="/privileges" className="text-[#c9a84c] hover:text-[#d4af37] transition-colors font-medium text-sm">Privileges</Link></li>
              <li><Link to="/honeymoon" className="text-rose-400 hover:text-rose-300 transition-colors font-medium text-sm" data-testid="footer-honeymoon-link">Honeymoon in Italy</Link></li>
              <li><Link to="/become-partner" className="text-gray-400 hover:text-white transition-colors text-sm">{language === 'it' ? 'Entra nella Rete' : language === 'es' ? 'Entra en la Red' : language === 'pt' ? 'Entre na Rede' : 'Join the Network'}</Link></li>
              <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors text-sm">{t.nav.blog}</Link></li>
              <li><Link to="/faq" className="text-gray-400 hover:text-white transition-colors text-sm">FAQ</Link></li>
              <li><Link to="/gallery" className="text-amber-400 hover:text-amber-300 transition-colors font-medium text-sm">{language === 'it' ? 'I Nostri Risultati' : language === 'es' ? 'Nuestros Resultados' : language === 'pt' ? 'Nossos Resultados' : 'Our Results'}</Link></li>
              <li><Link to="/italy-map" className="text-emerald-400 hover:text-emerald-300 transition-colors font-medium text-sm">{language === 'it' ? 'Mappa Origini' : language === 'es' ? 'Mapa Orígenes' : language === 'pt' ? 'Mapa Origens' : 'Origins Map'}</Link></li>
              <li><Link to="/donate" className="text-rose-400 hover:text-rose-300 transition-colors font-medium text-sm" data-testid="footer-donate-link">❤️ {language === 'it' ? 'Donazione Volontaria' : language === 'es' ? 'Donación Voluntaria' : language === 'pt' ? 'Doação Voluntária' : 'Voluntary Donation'}</Link></li>
              <li><Link to="/certificate" className="text-amber-400 hover:text-amber-300 transition-colors font-medium text-sm">{language === 'it' ? 'Certificato Genealogico' : language === 'es' ? 'Certificado' : language === 'pt' ? 'Certificado' : 'Certificate'}</Link></li>
              <li><Link to="/booking" className="text-green-400 hover:text-green-300 transition-colors font-medium text-sm">{language === 'it' ? 'Prenota Consulenza' : language === 'es' ? 'Reservar Consulta' : language === 'pt' ? 'Agendar Consulta' : 'Book Consultation'}</Link></li>
              <li><Link to="/reviews" className="text-yellow-400 hover:text-yellow-300 transition-colors font-medium text-sm">{language === 'it' ? 'Recensioni' : language === 'es' ? 'Reseñas' : language === 'pt' ? 'Avaliações' : 'Reviews'}</Link></li>
            </ul>
          </div>

          {/* Info & Servizi */}
          <div>
            <h3 className="font-semibold mb-4">{language === 'it' ? 'Info & Servizi' : language === 'es' ? 'Info y Servicios' : language === 'pt' ? 'Info e Serviços' : 'Info & Services'}</h3>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm">{t.nav.about}</Link></li>
              <li><Link to="/team" className="text-gray-400 hover:text-white transition-colors text-sm">Team</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">{t.nav.contact}</Link></li>
              <li><Link to="/research-hub" className="text-gray-400 hover:text-white transition-colors text-sm">{language === 'it' ? 'Ricerca & Pubblicazioni' : language === 'es' ? 'Investigación y Publicaciones' : language === 'pt' ? 'Pesquisa e Publicações' : 'Research & Publications'}</Link></li>
              <li><Link to="/italian-for-genealogy" className="text-gray-400 hover:text-white transition-colors text-sm">{language === 'it' ? 'Italiano per Genealogia' : language === 'es' ? 'Italiano para Genealogía' : language === 'pt' ? 'Italiano para Genealogia' : 'Italian for Genealogy'}</Link></li>
              <li><Link to="/heritage-recipes" className="text-gray-400 hover:text-white transition-colors text-sm">{language === 'it' ? 'Ricette Tradizionali' : language === 'es' ? 'Recetas Tradicionales' : language === 'pt' ? 'Receitas Tradicionais' : 'Heritage Recipes'}</Link></li>
              <li><Link to="/heritage-travel" className="text-gray-400 hover:text-white transition-colors text-sm">{language === 'it' ? 'Viaggio nelle Radici' : language === 'es' ? 'Viaje Ancestral' : language === 'pt' ? 'Viagem às Raízes' : 'Heritage Travel'}</Link></li>
              <li><Link to="/cemetery-photo-service" className="text-gray-400 hover:text-white transition-colors text-sm">{language === 'it' ? 'Foto Lapidi e Cimiteri' : language === 'es' ? 'Fotos de Cementerios' : language === 'pt' ? 'Fotos de Cemitérios' : 'Cemetery Photo Service'}</Link></li>
              <li><Link to="/surname-origins" className="text-gray-400 hover:text-white transition-colors text-sm">{language === 'it' ? 'Origine Cognomi' : language === 'es' ? 'Origen Apellidos' : language === 'pt' ? 'Origem Sobrenomes' : 'Surname Origins'}</Link></li>
              <li><Link to="/scan-records" className="text-gray-400 hover:text-white transition-colors text-sm">{language === 'it' ? 'Scansiona Documenti' : language === 'es' ? 'Escanear Documentos' : language === 'pt' ? 'Digitalizar Documentos' : 'Scan Records'}</Link></li>
              <li><Link to="/ancestral-places" className="text-gray-400 hover:text-white transition-colors text-sm">{language === 'it' ? 'Mappe Storiche' : language === 'es' ? 'Mapas Históricos' : language === 'pt' ? 'Mapas Históricos' : 'Historical Maps'}</Link></li>
              <li><Link to="/quick-help" className="text-gray-400 hover:text-white transition-colors text-sm">{language === 'it' ? 'Aiuto Rapido' : language === 'es' ? 'Ayuda Rápida' : language === 'pt' ? 'Ajuda Rápida' : 'Quick Help'}</Link></li>
              <li><Link to="/pricing" className="text-gray-400 hover:text-white transition-colors text-sm">{language === 'it' ? 'Genealogia' : language === 'es' ? 'Genealogía' : language === 'pt' ? 'Genealogia' : 'Genealogy'}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold mb-4">{t.footer.contactUs}</h3>
            <ul className="space-y-3">
              <li>
                <a href="mailto:italianrootsdiscovery@gmail.com" className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">italianrootsdiscovery@gmail.com</span>
                </a>
              </li>
              <li>
                <a href="tel:+393318792031" className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" />
                  <span>Stella Laura: +39 331 879 2031</span>
                </a>
              </li>
              <li>
                <a href="tel:+393920734627" className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" />
                  <span>Caliò Giosia: +39 392 073 4627</span>
                </a>
              </li>
              <li>
                <a 
                  href={`https://wa.me/393318792031?text=${encodeURIComponent('Ciao Laura! Vorrei informazioni sui servizi di Italian Roots Discovery.')}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-green-400 hover:text-green-300 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp Laura</span>
                </a>
              </li>
              <li>
                <a 
                  href={`https://wa.me/393920734627?text=${encodeURIComponent('Hola Giosia! Me gustaría información sobre los servicios de Italian Roots Discovery.')}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-green-400 hover:text-green-300 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp Giosia</span>
                </a>
              </li>
              <li className="flex items-start space-x-2 text-gray-400">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <span className="text-sm">Milano • Torino • Genova • Venezia • Padova • Roma • Napoli • Palermo</span>
              </li>
            </ul>
            <div className="flex space-x-4 mt-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61586293033791" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/italianrootsdiscovery/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.linkedin.com/in/italianroots-discovery-1081633a7" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          {/* Business identity strip — mandatory under art. 2250 c.c. */}
          <div className="bg-gray-950/50 rounded-lg px-4 py-3 mb-6 text-center" data-testid="footer-legal-identity">
            <p className="text-[11px] text-gray-400 leading-relaxed">
              <span className="font-semibold text-gray-300">{LEGAL_IDENTITY.denomination}</span>
              <span className="text-gray-600"> · </span>
              P.IVA / C.F. <span className="font-mono text-gray-300">{LEGAL_IDENTITY.vatId}</span>
              <span className="text-gray-600"> · </span>
              REA <span className="font-mono text-gray-300">{LEGAL_IDENTITY.rea}</span>
              <span className="text-gray-600"> · </span>
              PEC <a href={`mailto:${LEGAL_IDENTITY.pec}`} className="text-gray-300 hover:text-white transition-colors">{LEGAL_IDENTITY.pec}</a>
              <span className="text-gray-600"> · </span>
              <span className="text-gray-400">{LEGAL_IDENTITY.registeredOffice.street}, {LEGAL_IDENTITY.registeredOffice.zip} {LEGAL_IDENTITY.registeredOffice.city} ({LEGAL_IDENTITY.registeredOffice.province})</span>
            </p>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                {t.footer.terms}
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                {t.footer.privacy}
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/cookies" className="text-gray-400 hover:text-white transition-colors">
                {t.footer.cookies}
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/dati-legali" className="text-[#C9A84C] hover:text-[#dab861] transition-colors font-medium" data-testid="footer-legal-link">
                {language === 'it' ? 'Dati Legali' : language === 'es' ? 'Datos Legales' : language === 'pt' ? 'Dados Legais' : 'Legal Information'}
              </Link>
            </div>
            <p className="text-gray-400 text-center">
              © {new Date().getFullYear()} Italian Roots Discovery — Laura Stella & Giosia Caliò. {t.footer.rights}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
